import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import Hero from '../components/landing/Hero';
import CardOfWeekSection from '../components/landing/CardOfWeekSection';
import Consultors from '../components/landing/Consultors';
import HowItWorks from '../components/landing/HowItWorks';
import Plans from '../components/landing/Plans';
import FAQ from '../components/landing/FAQ';
import Contact from '../components/landing/Contact';
import LoginModal from '../components/landing/LoginModal';
import RegisterModal from '../components/landing/RegisterModal';
import ForgotPasswordModal from '../components/landing/ForgotPasswordModal';
import { setAuthToken, setUserData, getAuthToken, getUserData, logout, validateToken, fetchWithAuth } from '../utils/auth';
import StartChatModal from '../components/landing/StartChatModal';

export default function LandingPage() {
  const [activeCard, setActiveCard] = useState<{
    id: string;
    type: string;
    cardName: string;
    cardImage: string | null;
    message: string;
    sendDate: string;
    state: string;
  } | null>(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
  const [selectedConsultor, setSelectedConsultor] = useState<[string, string, string[]] | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userName, setUserName] = useState<string>('');
  const [startChatButtonClicked, setStartChatButtonClicked] = useState(false);
  const [isStartChatModalOpen, setIsStartChatModalOpen] = useState(false);
  const [credits, setCredits] = useState(0);
  const navigate = useNavigate();

  const logError = (...args: unknown[]) => {
    if (import.meta.env.DEV) {
      console.error(...args);
    }
  };

  const getActiveConversationId = async (): Promise<string | null> => {
    try {
      const response = await fetchWithAuth('/api/conversations/getAllUsersConversations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) return null;

      const data = await response.json();
      const conversations = Array.isArray(data?.conversations) ? data.conversations : [];
      const activeConversation = conversations.find((conversation: any) => !conversation.endDate);

      return activeConversation?.id || null;
    } catch (error) {
      logError('Error checking active conversation:', error);
      return null;
    }
  };



  // Check if user is already logged in on mount and validate token
  useEffect(() => {
    const loadActiveCard = async () => {
      try {
        const response = await fetch('/api/card-of-week/current');

        if (response.status === 404) {
          setActiveCard(null);
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch active card');
        }

        const data = await response.json();
        setActiveCard(data);
      } catch (error) {
        logError('Error fetching active card of week:', error);
        setActiveCard(null);
      }
    };

    loadActiveCard();
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const token = getAuthToken();
      const userData = getUserData();

      // console.log(console.log('initAuth - token:', !!token, 'userData:', !!userData);

      if (token && userData) {
        // Set state immediately from cookies so navbar updates right away
        setIsAuthenticated(true);
        setUserName(userData.name);

        // Fetch credits from API
        // console.log(console.log('Fetching credits from /api/credits/me...');
        try {
          const creditsResponse = await fetch('/api/credits/me', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          // console.log(console.log('Credits response status:', creditsResponse.status);

          if (creditsResponse.ok) {
            const creditsData = await creditsResponse.json();
            // console.log(console.log('Credits data:', creditsData);
            setCredits(creditsData.credits || 0);
          } else {
            console.warn('Credits fetch failed:', creditsResponse.status);
            setCredits(0);
          }
        } catch (error) {
          logError('Error fetching credits:', error);
          setCredits(0);
        }

        // Validate token with server in background
        const isValid = await validateToken();
        // console.log(console.log('Token validation result:', isValid);

        if (!isValid) {
          // Token was invalid and cleared by validateToken
          setIsAuthenticated(false);
          setUserName('');
          setCredits(0);
        }
      }
    };

    initAuth();
  }, []);

  // Periodically validate token (every 5 minutes)
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(async () => {
      const isValid = await validateToken();

      if (!isValid) {
        // Token expired or invalid - logout user
        setIsAuthenticated(false);
        setUserName('');
        setCredits(0);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleSelectConsultor = async (consultor: string, name: string, types: string[]) => {
    // console.log(console.log('=== handleSelectConsultor START ===');
    // console.log(console.log('Consultor:', consultor);

    // Read current auth state from cookies instead of state closure
    const token = getAuthToken();
    const userData = getUserData();
    const isCurrentlyAuthenticated = !!(token && userData);


    setSelectedConsultor([consultor, name, types]);
    // console.log(console.log(consultor);
    if (!isCurrentlyAuthenticated) {
      // User not logged in - open login modal
      setIsLoginModalOpen(true);
    } else {
      const activeConversationId = await getActiveConversationId();
      if (activeConversationId) {
        window.alert('Já tem uma consulta ativa. Irá voltar para a conversa em curso.');
        navigate(`/chat?chatId=${activeConversationId}`);
        return;
      }

      // User is authenticated - open start chat modal
      setIsStartChatModalOpen(true);
    }
  };

  const handleSelectPlan = (plan: string) => {
    navigate(`/payment/${plan.toUpperCase()}`);
  };

  const handleLogin = async (token: string, user: { id: string; email: string; name: string }) => {
    setAuthToken(token);
    setUserData(user);
    setIsLoginModalOpen(false);
    setIsAuthenticated(true);
    setUserName(user.name);

    // Fetch credits from API
    // console.log(console.log('handleLogin - Fetching credits from /api/credits/me...');
    try {
      const creditsResponse = await fetch('/api/credits/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // console.log(console.log('handleLogin - Credits response status:', creditsResponse.status);

      if (creditsResponse.ok) {
        const creditsData = await creditsResponse.json();
        // console.log(console.log('handleLogin - Credits data:', creditsData);
        setCredits(creditsData.credits || 0);
      } else {
        console.warn('handleLogin - Credits fetch failed:', creditsResponse.status);
        setCredits(0);
      }
    } catch (error) {
      logError('handleLogin - Error fetching credits:', error);
      setCredits(0);
    }

    // If a consultant was selected, redirect to chat
    if (selectedConsultor) {
      setIsStartChatModalOpen(true);
    }
  };

  const handleRegister = async (token: string, user: { id: string; email: string; name: string }) => {
    // console.log(console.log(user);
    setAuthToken(token);
    setUserData(user);
    setIsRegisterModalOpen(false);
    setIsAuthenticated(true);
    setUserName(user.name);

    // Fetch credits from API
    // console.log(console.log('handleRegister - Fetching credits from /api/credits/me...');
    try {
      const creditsResponse = await fetch('/api/credits/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // console.log(console.log('handleRegister - Credits response status:', creditsResponse.status);

      if (creditsResponse.ok) {
        const creditsData = await creditsResponse.json();
        // console.log(console.log('handleRegister - Credits data:', creditsData);
        setCredits(creditsData.credits || 0);
      } else {
        console.warn('handleRegister - Credits fetch failed:', creditsResponse.status);
        setCredits(0);
      }
    } catch (error) {
      logError('handleRegister - Error fetching credits:', error);
      setCredits(0);
    }

    // If a consultant was selected, open the start chat modal
    if (selectedConsultor) {
      setIsStartChatModalOpen(true);
    }
  };

  const handleLogout = () => {
    logout();
    setIsAuthenticated(false);
    setUserName('');
    setCredits(0);
  };

  const handleSwitchToRegister = () => {
    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsRegisterModalOpen(false);
    setIsLoginModalOpen(true);
  };

  const handleForgotPassword = () => {
    setIsLoginModalOpen(false);
    setIsForgotPasswordModalOpen(true);
  };

  const handleForgotPasswordSuccess = () => {
    setIsForgotPasswordModalOpen(false);
  };


  return (
    <div className="min-h-screen bg-[#020617] text-[#e5e7eb]">
      <Header
        isAuthenticated={isAuthenticated}
        userName={userName}
        credits={credits}
        onOpenLogin={() => setIsLoginModalOpen(true)}
        onLogout={handleLogout}
      />

      <main id="topo">
        <Hero onOpenLogin={() => setIsLoginModalOpen(true)} />

        {/* Dynamic campaign highlight */}
        {activeCard && <CardOfWeekSection card={activeCard} />}

        {/* Conversion path: offer -> trust -> process -> pricing -> objections */}
        <Consultors onSelectConsultor={handleSelectConsultor} />
        <HowItWorks />
        <Plans onSelectPlan={handleSelectPlan} />
        <FAQ />
        <Contact />

        {/* Terms Section */}
        <section id="termos" className="py-[60px] bg-[#020617] border-t border-b border-[#111827]">
          <div className="max-w-[1120px] mx-auto px-4">
            <h2 className="text-[1.8rem] mb-4 font-bold text-[#e5e7eb]">Condições de utilização</h2>
            <p className="text-[#cbd5f5] mb-4">
              O serviço destina-se a maiores de 18 anos. O acesso é individual e não pode ser partilhado.
              É proibida a utilização de bots ou automação para envio de mensagens, bem como
              linguagem abusiva ou ofensiva.
            </p>
            <a href="/termos.html" className="text-[0.9rem] underline underline-offset-2 text-[#a5b4fc] hover:text-[#c7d2fe]">
              Ver Termos completos
            </a>
          </div>
        </section>

        {/* Privacy Section */}
        <section id="privacidade" className="py-[60px] bg-[radial-gradient(circle_at_top,rgba(15,23,42,0.9),#020617_55%)]">
          <div className="max-w-[1120px] mx-auto px-4">
            <h2 className="text-[1.8rem] mb-4 font-bold text-[#e5e7eb]">Privacidade e proteção de dados</h2>
            <p className="text-[#cbd5f5] mb-4">
              Tratamos os seus dados pessoais de acordo com o RGPD. Utilizamos apenas a informação necessária
              para gestão da conta, faturação e melhoria do serviço.
            </p>
            <a href="/privacidade.html" className="text-[0.9rem] underline underline-offset-2 text-[#a5b4fc] hover:text-[#c7d2fe]">
              Ver Política de Privacidade completa
            </a>
          </div>
        </section>
      </main>

      <Footer />

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => {
          setIsLoginModalOpen(false);
          setSelectedConsultor(null);

        }}
        onLogin={handleLogin}
        onSwitchToRegister={handleSwitchToRegister}
        onForgotPassword={handleForgotPassword}
      />

      <RegisterModal
        isOpen={isRegisterModalOpen}
        onClose={() => {
          setIsRegisterModalOpen(false);
          setSelectedConsultor(null);
        }}
        onRegister={handleRegister}
        onSwitchToLogin={handleSwitchToLogin}
      />

      <StartChatModal
        name={selectedConsultor ? selectedConsultor[1] : 'o consultor'}
        minutes={credits}
        isOpen={isStartChatModalOpen}
        isStarting={startChatButtonClicked}
        onClose={() => setIsStartChatModalOpen(false)}
        onStart={async () => {
          setStartChatButtonClicked(true);

          const response = await fetchWithAuth('/api/conversations/new', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              consultantName: selectedConsultor ? selectedConsultor[1] : '',
              consultantType: selectedConsultor ? selectedConsultor[2] : [],
              consultantId: selectedConsultor ? selectedConsultor[0] : '',
              maxDuration: credits
            })
          });

          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));

            if (response.status === 409 && payload?.activeConversationId) {
              setIsStartChatModalOpen(false);
              setSelectedConsultor(null);
              setStartChatButtonClicked(false);
              window.alert('Já existe uma consulta ativa. Irá voltar para essa consulta.');
              navigate(`/chat?chatId=${payload.activeConversationId}`);
              return;
            }

            setStartChatButtonClicked(false);
            window.alert(payload?.error || 'Não foi possível iniciar a consulta neste momento.');
            return;
          }

          const getChatId: { conversationId: string } = await response.json();

          setSelectedConsultor(null);

          navigate('/chat?chatId=' + getChatId.conversationId);
        }}
      />

      <ForgotPasswordModal
        isOpen={isForgotPasswordModalOpen}
        onClose={() => setIsForgotPasswordModalOpen(false)}
        onSuccess={handleForgotPasswordSuccess}
      />
    </div>
  );
}
